import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, resolveSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LogOut,
  Plus,
  Trash2,
  KeyRound,
  Image as ImageIcon,
  Video,
  Link as LinkIcon,
  Upload,
  X,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_bf707ab1-d809-4aca-a466-4390571c997b/artifacts/6hmyyhtk_23Recurso%203.png";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState("projects");

  const refresh = useCallback(async () => {
    const [u, p] = await Promise.all([api.get("/admin/users"), api.get("/admin/projects")]);
    setUsers(u.data);
    setProjects(p.data);
  }, []);

  useEffect(() => {
    refresh().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [refresh]);

  return (
    <div className="min-h-screen bg-[#05080f] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#05080f]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-9 w-9 object-contain" draggable={false} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">
                Panel administrador
              </div>
              <div className="text-sm font-medium text-white/90">{user?.username}</div>
            </div>
          </div>
          <Button
            data-testid="admin-logout-button"
            variant="ghost"
            onClick={logout}
            className="text-white/70 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Salir
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-white/[0.04]">
            <TabsTrigger
              data-testid="tab-projects"
              value="projects"
              className="data-[state=active]:bg-[#1e88ff] data-[state=active]:text-white"
            >
              Proyectos
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-users"
              value="users"
              className="data-[state=active]:bg-[#1e88ff] data-[state=active]:text-white"
            >
              Usuarios
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersTab users={users} refresh={refresh} />
          </TabsContent>

          <TabsContent value="projects">
            <ProjectsTab projects={projects} users={users} refresh={refresh} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ============== USERS ============== */
function UsersTab({ users, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "client" });
  const [pwdOpen, setPwdOpen] = useState(null); // user object
  const [newPwd, setNewPwd] = useState("");

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Usuario creado");
      setForm({ username: "", password: "", name: "", role: "client" });
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar usuario? Se borrarán también sus proyectos.")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Usuario eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetPwd = async () => {
    try {
      await api.post(`/admin/users/${pwdOpen.id}/password`, { password: newPwd });
      toast.success("Contraseña actualizada");
      setPwdOpen(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-serif text-2xl text-white/90">Usuarios</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-user-button"
              className="bg-[#1e88ff] text-white hover:bg-[#0f6fe5]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Crear usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="border-white/10 bg-[#05080f] text-white">
            <DialogHeader>
              <DialogTitle>Nuevo usuario</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <FieldInput
                id="cu-username"
                label="Usuario"
                value={form.username}
                onChange={(v) => setForm({ ...form, username: v })}
                required
                testid="new-user-username"
              />
              <FieldInput
                id="cu-name"
                label="Nombre visible"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid="new-user-name"
              />
              <FieldInput
                id="cu-password"
                label="Keypass"
                type="password"
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                required
                testid="new-user-password"
              />
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-white/50">Rol</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v })}
                >
                  <SelectTrigger className="mt-2 border-white/10 bg-black/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#05080f] text-white">
                    <SelectItem value="client">Cliente</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="submit-new-user"
                  className="bg-[#1e88ff] text-white hover:bg-[#0f6fe5]"
                >
                  Crear
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/5">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-left text-[10px] uppercase tracking-[0.2em] text-white/40">
            <tr>
              <th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="border-t border-white/5 hover:bg-white/[0.02]"
              >
                <td className="px-4 py-3 font-medium text-white/90">{u.username}</td>
                <td className="px-4 py-3 text-white/70">{u.name}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
                      u.role === "admin"
                        ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
                        : "border-[#1e88ff]/30 bg-[#1e88ff]/10 text-[#7cc4ff]"
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPwdOpen(u)}
                    className="text-white/70 hover:bg-white/5 hover:text-white"
                    data-testid={`reset-pwd-${u.id}`}
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => del(u.id)}
                    className="text-red-300 hover:bg-red-400/10 hover:text-red-200"
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-white/40">
                  No hay usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pwdOpen} onOpenChange={(o) => !o && setPwdOpen(null)}>
        <DialogContent className="border-white/10 bg-[#05080f] text-white">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña de {pwdOpen?.username}</DialogTitle>
          </DialogHeader>
          <FieldInput
            id="new-pwd"
            label="Nueva keypass"
            type="password"
            value={newPwd}
            onChange={setNewPwd}
            testid="reset-pwd-input"
          />
          <DialogFooter>
            <Button onClick={resetPwd} className="bg-[#1e88ff] text-white" data-testid="reset-pwd-submit">
              Actualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============== PROJECTS ============== */
function ProjectsTab({ projects, users, refresh }) {
  const [editing, setEditing] = useState(null); // project or "new"
  const clients = users.filter((u) => u.role === "client");

  const del = async (id) => {
    if (!window.confirm("¿Eliminar proyecto?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Proyecto eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-serif text-2xl text-white/90">Proyectos</h2>
        <Button
          data-testid="create-project-button"
          className="bg-[#1e88ff] text-white hover:bg-[#0f6fe5]"
          onClick={() =>
            setEditing({
              isNew: true,
              client_id: clients[0]?.id || "",
              title: "",
              description: "",
              items: [],
            })
          }
          disabled={clients.length === 0}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuevo proyecto
        </Button>
      </div>

      {clients.length === 0 && (
        <div className="mb-6 rounded-md border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200">
          Crea primero un usuario "cliente" en la pestaña Usuarios.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => {
          const owner = users.find((u) => u.id === p.client_id);
          return (
            <div
              key={p.id}
              data-testid={`admin-project-${p.id}`}
              className="rounded-xl border border-white/5 bg-white/[0.02] p-5"
            >
              <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                {owner ? owner.username : "Sin cliente"}
              </div>
              <h3 className="font-serif text-lg text-white/90">{p.title}</h3>
              {p.description && (
                <p className="mt-2 line-clamp-2 text-xs text-white/50">{p.description}</p>
              )}
              <div className="mt-3 text-[11px] text-white/40">
                {p.items?.length || 0} vistas previas
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  onClick={() => setEditing({ ...p })}
                  data-testid={`edit-project-${p.id}`}
                >
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-300 hover:bg-red-400/10 hover:text-red-200"
                  onClick={() => del(p.id)}
                  data-testid={`delete-project-${p.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
        {projects.length === 0 && (
          <div className="col-span-full rounded-lg border border-white/5 bg-white/[0.02] p-10 text-center text-sm text-white/50">
            Aún no hay proyectos.
          </div>
        )}
      </div>

      {editing && (
        <ProjectEditor
          project={editing}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function ProjectEditor({ project, clients, onClose, onSaved }) {
  const [form, setForm] = useState(project);
  const [busy, setBusy] = useState(false);

  const updateItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };

  const addItem = (type) => {
    setForm({
      ...form,
      items: [
        ...form.items,
        { id: crypto.randomUUID(), type, src: "", label: "" },
      ],
    });
  };

  const removeItem = (idx) => {
    const items = [...form.items];
    items.splice(idx, 1);
    setForm({ ...form, items });
  };

  const uploadFor = async (idx, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateItem(idx, { type: data.type, src: data.src });
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        client_id: form.client_id,
        title: form.title,
        description: form.description,
        items: form.items.map((it) => ({
          id: it.id,
          type: it.type,
          src: it.src,
          label: it.label || null,
        })),
      };
      if (form.isNew) {
        await api.post("/admin/projects", payload);
        toast.success("Proyecto creado");
      } else {
        await api.put(`/admin/projects/${form.id}`, payload);
        toast.success("Proyecto actualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#05080f] text-white">
        <DialogHeader>
          <DialogTitle>{form.isNew ? "Nuevo proyecto" : "Editar proyecto"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-white/50">Cliente</Label>
            <Select
              value={form.client_id}
              onValueChange={(v) => setForm({ ...form, client_id: v })}
            >
              <SelectTrigger className="mt-2 border-white/10 bg-black/30 text-white" data-testid="project-client-select">
                <SelectValue placeholder="Selecciona un cliente" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#05080f] text-white">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name || c.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <FieldInput
            id="proj-title"
            label="Título"
            value={form.title}
            onChange={(v) => setForm({ ...form, title: v })}
            testid="project-title-input"
          />

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-white/50">Descripción</Label>
            <Textarea
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 border-white/10 bg-black/30 text-white"
              rows={3}
              data-testid="project-description-input"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.2em] text-white/50">
                Vistas previas
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-white/10 bg-transparent text-white/80"
                  onClick={() => addItem("image")}
                  data-testid="add-image-item"
                >
                  <ImageIcon className="mr-1 h-3.5 w-3.5" /> Imagen
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-white/10 bg-transparent text-white/80"
                  onClick={() => addItem("video")}
                  data-testid="add-video-item"
                >
                  <Video className="mr-1 h-3.5 w-3.5" /> Video
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-white/10 bg-transparent text-white/80"
                  onClick={() => addItem("url")}
                  data-testid="add-url-item"
                >
                  <LinkIcon className="mr-1 h-3.5 w-3.5" /> URL
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <div
                  key={it.id}
                  className="rounded-lg border border-white/5 bg-white/[0.02] p-3"
                  data-testid={`item-row-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-20 shrink-0">
                      {it.src && (it.type === "image") && (
                        <img
                          src={resolveSrc(it.src)}
                          alt=""
                          className="h-16 w-20 rounded object-cover"
                        />
                      )}
                      {it.src && it.type === "video" && (
                        <video src={resolveSrc(it.src)} className="h-16 w-20 rounded object-cover" />
                      )}
                      {(!it.src || it.type === "url") && (
                        <div className="flex h-16 w-20 items-center justify-center rounded bg-black/30 text-white/40">
                          {it.type === "image" && <ImageIcon className="h-5 w-5" />}
                          {it.type === "video" && <Video className="h-5 w-5" />}
                          {it.type === "url" && <LinkIcon className="h-5 w-5" />}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      <Input
                        value={it.label || ""}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                        placeholder="Etiqueta (opcional)"
                        className="h-9 border-white/10 bg-black/30 text-sm text-white"
                      />
                      <Input
                        value={it.src}
                        onChange={(e) => updateItem(idx, { src: e.target.value })}
                        placeholder={
                          it.type === "url"
                            ? "https://..."
                            : "URL externa o sube archivo →"
                        }
                        className="h-9 border-white/10 bg-black/30 text-sm text-white"
                      />
                      {it.type !== "url" && (
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/80 hover:bg-white/[0.08]">
                          <Upload className="h-3.5 w-3.5" />
                          Subir {it.type === "image" ? "imagen" : "video"}
                          <input
                            type="file"
                            className="hidden"
                            accept={it.type === "image" ? "image/*" : "video/*"}
                            onChange={(e) => e.target.files?.[0] && uploadFor(idx, e.target.files[0])}
                          />
                        </label>
                      )}
                    </div>

                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeItem(idx)}
                      className="text-red-300 hover:bg-red-400/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {form.items.length === 0 && (
                <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.02] p-6 text-center text-xs text-white/40">
                  Agrega imágenes, videos o URLs.
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-white/70">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={busy || !form.title || !form.client_id}
            className="bg-[#1e88ff] text-white hover:bg-[#0f6fe5]"
            data-testid="save-project-button"
          >
            {busy ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============== Small UI ============== */
function FieldInput({ id, label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-white/50">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 h-11 border-white/10 bg-black/30 text-white"
      />
    </div>
  );
}
